import type { MissingField } from '@sitzmark/core';
import { fillableGaps, tryParseMoney } from '@sitzmark/core';

/**
 * The pure half of the guided finish-this-product pass (revision item 39).
 *
 * The dialog walks one style at a time asking only for what is missing; this
 * module decides *what* is missing from the freshly-loaded detail and turns
 * the operator's answers into the exact patches the API takes. Extracted so
 * the fan-out rules — one bin answer covering every unbinned size, one target
 * covering every untargeted one, a retail per unpriced variant — are tested
 * as functions rather than asserted through a rendered dialog.
 */

export interface FinishableProduct {
  /** The category it is filed in (decision 214). */
  departmentId: string | null;
  brandId: string | null;
  season: string | null;
  description: string | null;
  trackInventory: boolean;
}

export interface FinishableVariant {
  id: string;
  active: boolean;
  retailCents: number;
  msrpCents: number | null;
  binLocation: string | null;
  targetQty: number | null;
}

/** What this style still owes, computed from the live detail — never a stale
 * queue snapshot, because someone may have finished half of it since. */
export function askedFields(
  product: FinishableProduct,
  variants: readonly FinishableVariant[],
): MissingField[] {
  const live = variants.filter((variant) => variant.active);
  const blankBin = (bin: string | null) => bin === null || bin.trim() === '';
  // Everything the pass can fill, optional gaps included; only some are owed.
  return fillableGaps({
    departmentId: product.departmentId,
    brandId: product.brandId,
    season: product.season,
    description: product.description,
    trackInventory: product.trackInventory,
    unpricedVariants: live.filter((variant) => variant.retailCents === 0).length,
    unbinnedVariants: live.filter((variant) => blankBin(variant.binLocation)).length,
    untargetedVariants: live.filter((variant) => variant.targetQty === null).length,
  });
}

export interface StyleAnswers {
  departmentId: string;
  brandId: string;
  season: string;
  description: string;
}

/**
 * Only what was asked **and** answered. An empty answer is a skip, not an
 * erasure — the pass must never write a blank over a field it did not ask
 * about, and never write a blank at all.
 */
export function buildStylePatch(
  asked: readonly MissingField[],
  answers: StyleAnswers,
): {
  departmentId?: string;
  brandId?: string;
  season?: string;
  description?: string;
} | null {
  const patch: ReturnType<typeof buildStylePatch> = {};
  if (asked.includes('category') && answers.departmentId !== '')
    patch.departmentId = answers.departmentId;
  if (asked.includes('brand') && answers.brandId !== '') patch.brandId = answers.brandId;
  if (asked.includes('season') && answers.season.trim() !== '')
    patch.season = answers.season.trim();
  if (asked.includes('description') && answers.description.trim() !== '') {
    patch.description = answers.description.trim();
  }
  return Object.keys(patch).length === 0 ? null : patch;
}

export interface VariantAnswers {
  /** Retail drafts keyed by variant id — only unpriced variants get inputs. */
  retail: Record<string, string>;
  /** One bin for every size that has none. */
  bin: string;
  /** One target quantity for every size that has none. */
  target: string;
}

export interface VariantPatch {
  variantId: string;
  retailCents?: number;
  binLocation?: string;
  targetQty?: number;
}

/**
 * The variant patches one answered step produces, or the reason it cannot.
 *
 * Fan-out is per *gap*, not per style: the bin answer touches only the sizes
 * missing one, so a style half-binned by hand keeps what somebody already
 * decided. A filled-but-unparseable answer is a `problem`, never a silent
 * skip — a target of "1o" must not quietly leave 35 sizes untargeted.
 */
export function buildVariantPatches(
  asked: readonly MissingField[],
  variants: readonly FinishableVariant[],
  answers: VariantAnswers,
): { patches: VariantPatch[]; problem?: string } {
  const live = variants.filter((variant) => variant.active);
  const byId = new Map<string, VariantPatch>();
  const patchFor = (variantId: string) => {
    const existing = byId.get(variantId);
    if (existing !== undefined) return existing;
    const created: VariantPatch = { variantId };
    byId.set(variantId, created);
    return created;
  };

  if (asked.includes('retail')) {
    for (const variant of live) {
      if (variant.retailCents !== 0) continue;
      const raw = answers.retail[variant.id] ?? '';
      if (raw.trim() === '') continue;
      const cents = tryParseMoney(raw);
      if (cents === null) return { patches: [], problem: 'One of those prices is not a number' };
      if (cents > 0) patchFor(variant.id).retailCents = cents;
    }
  }

  if (asked.includes('bin') && answers.bin.trim() !== '') {
    for (const variant of live) {
      if (variant.binLocation === null || variant.binLocation.trim() === '') {
        patchFor(variant.id).binLocation = answers.bin.trim();
      }
    }
  }

  if (asked.includes('targets') && answers.target.trim() !== '') {
    const target = Number(answers.target.trim());
    if (!Number.isInteger(target) || target < 0) {
      return { patches: [], problem: 'The target has to be a whole number of units' };
    }
    for (const variant of live) {
      if (variant.targetQty === null) patchFor(variant.id).targetQty = target;
    }
  }

  return { patches: [...byId.values()] };
}

/**
 * The pass's queue, with what was skipped this visit moved to the back.
 *
 * The queue is oldest first, so reopening the pass after an evening of Skip
 * put every skipped style back at the front: "no different than ten minutes
 * ago" (FB-1073). Skipped ones still come round — last, after everything not
 * yet looked at. Stable: within each part the server's order is kept.
 */
export function orderQueue<T extends { readonly id: string }>(
  styles: readonly T[],
  skipped: ReadonlySet<string>,
): T[] {
  return [
    ...styles.filter((style) => !skipped.has(style.id)),
    ...styles.filter((style) => skipped.has(style.id)),
  ];
}
